import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ModalInfoType } from '@components/molecules/modal/Modal'

import type {
	PaymentStateType,
	ApiGetDataStatus,
	PaymentCustomerInfoType,
	PaymentDeliveryType,
	PaymentShippingType,
	PaymentCardType,
	PaymentCostType,
} from '@redux/types'
import { ShippingCostType } from '@schemas'

export const unsupportedPayPalCountries = [
	'AF', // 아프가니스탄
	'BD', // 방글라데시
	'CM', // 카메룬
	'CF', // 중앙아프리카공화국
	'CI', // 코트디부아르
	'CU', // 쿠바
	'IR', // 이란
	'IQ', // 이라크
	'KP', // 북한
	'LB', // 레바논
	'LR', // 라이베리아
	'LY', // 리비아
	'MM', // 미얀마
	'PK', // 파키스탄
	'SD', // 수단
	'SY', // 시리아
	'ZW', // 짐바브웨
	'BY', // 벨라루스
	'BO', // 볼리비아
	'ET', // 에티오피아
	'GH', // 가나
	'NE', // 니제르
	'NG', // 나이지리아
	'LK', // 스리랑카
	'TG', // 토고
	'TM', // 투르크메니스탄
	'UA', // 우크라이나
	'UZ', // 우즈베키스탄
	'VE', // 베네수엘라
	'VN', // 베트남
	'YE', // 예멘
	'KR', // 한국
]

// initial state
const initialState: PaymentStateType = {
	triggerInitPaymentPage: false,
	triggerUsdPayNow: false,
	triggerKrwPayNow: false,
	triggerCouponApply: false,
	triggerGetShippingCost: false,
	triggerResetPaymentData: false,

	paymentStatus: 'init',
	tryToPayment: {
		cartList: [],
	},

	showModal: false,
	modalInfo: {
		target: '',
		title: '',
	},

	// shipping cost
	shippingCostList: [],
	shippingCostStatus: 'init',

	// payment data
	paymentMethod: '',
	paymentCustomerInfo: {
		givenName: '',
		familyName: '',
		email: '',
		phoneCountryCode: '',
		phoneNumber: '',
	},
	paymentDelivery: {
		countryName: '',
		countryIsoCode: '',
		stateName: '',
		stateIsoCode: '',
		city: '',
		street1: '',
		street2: '',
		zip: '',
	},
	paymentShipping: {
		companyName: '',
		deliveryOption: '',
		usd: 0,
		deliveryDays: 0,
		boxType: 'A',
		rateType: '',
	},
	paymentCard: {
		cardNumber: '',
		expiredMonth: '',
		expiredYear: '',
		cvc: '',
		cardName: '',
	},
	paymentCost: {
		totalUsd: 0,
		vatUsd: 0,
		shippingUsd: 0,
		discountedUsd: 0,
		discountedKrw: 0,
		paidUsd: 0,
		couponCode: '',
	},

	// payment validation
	paymentCouponErrorMessage: '',
	paymentErrorMessages: {
		customer: {
			givenName: '',
			familyName: '',
			email: '',
			phoneCountryCode: '',
			phoneNumber: '',
		},
		delivery: {
			countryName: '',
			states: '',
			city: '',
			street1: '',
			zip: '',
		},
		shipping: {
			companyName: '',
		},
		card: {
			cardNumber: '',
			expiredMonth: '',
			expiredYear: '',
			cvc: '',
			cardName: '',
		},
		isSuccess: false,
	},
	paymentSuccess: {
		orderId: '',
		orderDetail: '',
		address: '',
		email: '',
		phoneNumber: '',
	},
}

const slice = createSlice({
	name: 'payment',
	initialState,
	reducers: {
		TRIGGER_INIT_PAYMENT_PAGE: (
			state: PaymentStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerInitPaymentPage = action.payload
		},
		TRIGGER_USD_PAY_NOW: (state: PaymentStateType, action: PayloadAction<boolean>) => {
			state.triggerUsdPayNow = action.payload
		},
		TRIGGER_KRW_PAY_NOW: (state: PaymentStateType, action: PayloadAction<boolean>) => {
			state.triggerKrwPayNow = action.payload
		},
		TRIGGER_COUPON_APPLY: (state: PaymentStateType, action: PayloadAction<boolean>) => {
			state.triggerCouponApply = action.payload
		},
		TRIGGER_GET_SHIPPING_COST: (
			state: PaymentStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerGetShippingCost = action.payload
		},
		TRIGGER_RESET_PAYMENT_DATA: (
			state: PaymentStateType,
			action: PayloadAction<boolean>,
		) => {
			state.triggerResetPaymentData = action.payload
		},

		PAYMENT_STATUS: (
			state: PaymentStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.paymentStatus = action.payload
		},
		TRY_TO_PAYMENT: (
			state: PaymentStateType,
			action: PayloadAction<PaymentStateType['tryToPayment']>,
		) => {
			state.tryToPayment = action.payload
		},

		SHOW_MODAL: (state: PaymentStateType, action: PayloadAction<boolean>) => {
			state.showModal = action.payload
		},
		MODAL_INFO: (state: PaymentStateType, action: PayloadAction<ModalInfoType>) => {
			state.modalInfo = action.payload
		},

		// shipping cost
		SHIPPING_COST_LIST: (
			state: PaymentStateType,
			action: PayloadAction<ShippingCostType[]>,
		) => {
			state.shippingCostList = action.payload
		},
		SHIPPING_COST_STATUS: (
			state: PaymentStateType,
			action: PayloadAction<ApiGetDataStatus>,
		) => {
			state.shippingCostStatus = action.payload
		},

		// payment data
		PAYMENT_METHOD: (state: PaymentStateType, action: PayloadAction<string>) => {
			state.paymentMethod = action.payload
		},
		PAYMENT_CUSTOMER_INFO: (
			state: PaymentStateType,
			action: PayloadAction<PaymentCustomerInfoType>,
		) => {
			state.paymentCustomerInfo = action.payload
		},
		PAYMENT_DELIVERY: (
			state: PaymentStateType,
			action: PayloadAction<PaymentDeliveryType>,
		) => {
			state.paymentDelivery = action.payload
		},
		PAYMENT_SHIPPING: (
			state: PaymentStateType,
			action: PayloadAction<PaymentShippingType>,
		) => {
			state.paymentShipping = action.payload
		},
		PAYMENT_CARD: (state: PaymentStateType, action: PayloadAction<PaymentCardType>) => {
			state.paymentCard = action.payload
		},
		PAYMENT_COST: (state: PaymentStateType, action: PayloadAction<PaymentCostType>) => {
			state.paymentCost = action.payload
		},

		PAYMENT_COUPON_ERROR_MESSAGE: (
			state: PaymentStateType,
			action: PayloadAction<string>,
		) => {
			state.paymentCouponErrorMessage = action.payload
		},
		PAYMENT_ERROR_MESSAGES: (
			state: PaymentStateType,
			action: PayloadAction<PaymentStateType['paymentErrorMessages']>,
		) => {
			state.paymentErrorMessages = action.payload
		},
		PAYMENT_SUCCESS: (
			state: PaymentStateType,
			action: PayloadAction<PaymentStateType['paymentSuccess']>,
		) => {
			state.paymentSuccess = action.payload
		},
	},
})

export const paymentActions = slice.actions
export default slice.reducer
